import {
  forwardRef,
  useMemo,
  type ForwardedRef,
  type ReactNode,
  type Ref,
} from "react";
import {
  DataGrid,
  initIdsToPin,
  useDataGrid,
  type PickedTableOptions,
} from "@jana-technology/platform-ui";

import type { DataTableProps } from "./types";

const DataTableRender = <TData,>(
  { tableOptions, ...tableProps }: DataTableProps<TData>,
  ref: ForwardedRef<HTMLDivElement>,
) => {
  const optionsMemo: PickedTableOptions<TData> = useMemo(() => {
    const pinnedColumns = tableOptions.columns.reduce(
      (acc: Set<string>, column) => {
        initIdsToPin(column, acc);

        return acc;
      },
      new Set<string>(),
    );

    return {
      defaultColumn: {
        minSize: 60,
        size: 120,
      },
      initialState: {
        columnPinning: {
          left: Array.from(pinnedColumns),
        },
        ...tableOptions.initialState,
      },
      ...tableOptions,
    };
  }, [tableOptions]);

  const table = useDataGrid<TData>(optionsMemo);

  return <DataGrid ref={ref} table={table} {...tableProps} />;
};

export const DataTable = forwardRef(DataTableRender) as <TData>(
  props: DataTableProps<TData> & {
    ref?: Ref<HTMLDivElement>;
  },
) => ReactNode;
